import type { HTMLAttributes, MouseEvent } from 'react';

import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Chip, IconButton, Skeleton, Stack, Typography } from '@mui/material';

import { RoleListItemContent } from 'pages/UsersPage/UsersTable/UsersTableRoleSelectionCell/RoleListItemCell';

import type { SxProps, Theme } from '@mui/material/styles';
import type { AccessCentreRole, AccessCentreRolePermissionSummary } from 'domains/accessCentre/types';

export const bulkRoleAutocompleteOptionStackSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    typography: 'body1',
    fontFamily: 'inherit',
    alignItems: 'stretch',
    py: 0.25,
};

const formatModuleLabel = (module: string) => module.replace(/_/g, ' ');

const stopListboxEvent = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
};

export type BulkRoleAutocompleteOptionProps = {
    props: HTMLAttributes<HTMLLIElement>;
    role: AccessCentreRole;
    isExpanded: boolean;
    onToggleExpand: () => void;
    details: AccessCentreRolePermissionSummary | undefined;
    isDetailsLoading: boolean;
    isDetailsError: boolean;
};

const BulkRoleAutocompleteOption = ({
    props,
    role,
    isExpanded,
    onToggleExpand,
    details,
    isDetailsLoading,
    isDetailsError,
}: BulkRoleAutocompleteOptionProps) => {
    const { key, ...liProps } = props as HTMLAttributes<HTMLLIElement> & { key?: string };

    const handleExpandClick = (e: MouseEvent) => {
        stopListboxEvent(e);
        onToggleExpand();
    };

    return (
        <li key={key} {...liProps}>
            <Stack direction="column" spacing={1} sx={{ py: 0.5, width: '100%', minWidth: 0 }}>
                <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <RoleListItemContent role={role} showUserCount stackSx={bulkRoleAutocompleteOptionStackSx} />
                    </Box>
                    <IconButton
                        size="small"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse role permission summary' : 'Expand role permission summary'}
                        onMouseDown={stopListboxEvent}
                        onClick={handleExpandClick}
                        data-testid={`access-center-bulk-role-expand-${role.id}`}
                        sx={{ mt: 0.25, flexShrink: 0 }}
                    >
                        <ExpandMore
                            color="primary"
                            sx={{
                                transition: (theme) => theme.transitions.create('transform', { duration: 200 }),
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        />
                    </IconButton>
                </Stack>
                {isExpanded && (
                    <Box sx={{ pl: 0.5, pr: 1, pb: 0.5 }} data-testid={`access-center-bulk-role-details-${role.id}`}>
                        {isDetailsLoading && (
                            <Stack
                                spacing={1}
                                alignItems="flex-start"
                                role="status"
                                aria-label="Loading permission summary"
                            >
                                <Skeleton variant="text" width={220} height={20} sx={{ maxWidth: '100%' }} />
                                <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                                    <Skeleton variant="rounded" width={92} height={24} />
                                    <Skeleton variant="rounded" width={108} height={24} />
                                    <Skeleton variant="rounded" width={84} height={24} />
                                </Stack>
                            </Stack>
                        )}
                        {isDetailsError && !isDetailsLoading && (
                            <Typography variant="body2" color="error">
                                Could not load permission summary.
                            </Typography>
                        )}
                        {!isDetailsLoading && !isDetailsError && details && (
                            <Stack spacing={1} alignItems="flex-start">
                                <Typography variant="body2" color="text.secondary">
                                    {details.module_count} {details.module_count === 1 ? 'Module' : 'Modules'},{' '}
                                    {details.permission_count}{' '}
                                    {details.permission_count === 1 ? 'Permission' : 'Permissions'}
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={0.8} useFlexGap>
                                    {details.module_wise_permission_count.map((row) => (
                                        <Chip
                                            key={row.module}
                                            variant="outlined"
                                            size="small"
                                            label={`${formatModuleLabel(row.module)} (${row.count})`}
                                            sx={(theme) => ({
                                                height: '24px',
                                                minHeight: '24px',
                                                borderRadius: '6px',
                                                border: `1px solid ${theme.palette.other.divider}`,
                                                backgroundColor: 'transparent',
                                                color: theme.palette.text.secondary,
                                                fontWeight: 400,
                                                '& .MuiChip-label': { py: 0, px: 1, lineHeight: 1.25 },
                                            })}
                                            data-testid={`access-center-bulk-role-module-chip-${role.id}-${row.module}`}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        )}
                    </Box>
                )}
            </Stack>
        </li>
    );
};

export default BulkRoleAutocompleteOption;
